import { b2x } from '@b2x/react/src';
import React from 'react';

import { customerIsVet } from './util';

export interface AddToCartQuantitySelectProps {
  ariaLabel?: string;
  className?: string;
  fieldsHelper: b2x.AddToCartFieldsHelper;
  formGroupClassName?: string;
  selectedSku?: b2x.SkuApiDto;
  style?: React.CSSProperties;
}

/**
 * Select della quantità per l'add to cart.
 *
 * Per gli utenti vet, che vedono lo stock residuo tramite StockAvailability, limita le opzioni
 * allo stock disponibile dello sku selezionato. Se lo stock non è noto (warehouseSkus non
 * popolati) o è pari a 0 non si limita nulla: l'acquistabilità resta governata da sku.state.
 *
 * Il limite è applicato solo qui, tramite le options e il clamp del valore formik.
 * fieldsHelper.quantity.numberInput e fieldsHelper.buttons.quantity.increase restano legati ad
 * appConfig.productMaxQuantity, ma purina non li utilizza (ProductStickyFooter variante 'B'
 * mostra la quantità in sola lettura). Se in futuro venissero adottati, il limite va esteso.
 */
export const AddToCartQuantitySelect = ({
  ariaLabel,
  className,
  fieldsHelper,
  formGroupClassName,
  selectedSku,
  style,
}: AddToCartQuantitySelectProps) => {
  const { session } = b2x.useAppContext();
  const { setFieldValue, values } = b2x.useFormikContext<b2x.AddToCartFormValues>();
  const { availability } = b2x.useStockAvailability(selectedSku);

  const isVet = session?.customer?.qualifiers !== undefined && customerIsVet(session.customer.qualifiers);

  const maxQuantity = React.useMemo(() => {
    const configMaxQuantity = b2x.appConfig.productMaxQuantity ?? 99;
    if (!isVet || availability === undefined || availability <= 0) {
      return configMaxQuantity;
    }
    return Math.min(availability, configMaxQuantity);
  }, [availability, isVet]);

  const options = React.useMemo(
    () => [...Array(maxQuantity)].map((quantity, index) => ({ label: `${index + 1}`, value: `${index + 1}` })),
    [maxQuantity]
  );

  const quantity = b2x.getFormikNumberValue(values.quantity);

  /*
    Se lo sku selezionato cambia senza che formik si reinizializzi (accade cambiando sku all'interno
    della stessa variante, dove enableReinitialize non scatta perché _initialValues resta invariato),
    la quantità già scelta può risultare fuori dalle nuove options. In quel caso la select nativa
    apparirebbe vuota ma il valore verrebbe comunque inviato al submit, quindi lo riportiamo al massimo.
    useLayoutEffect e non useEffect per correggerlo prima del paint.
  */
  React.useLayoutEffect(() => {
    if (quantity !== undefined && quantity > maxQuantity) {
      setFieldValue('quantity', maxQuantity);
    }
  }, [maxQuantity, quantity, setFieldValue]);

  return (
    <b2x.FormGroup {...fieldsHelper.quantity.formGroup} className={formGroupClassName} label={undefined} noMarginBottom>
      <b2x.Select
        {...fieldsHelper.quantity.select}
        aria-label={ariaLabel}
        className={className}
        includeEmptyOption={false}
        options={options}
        placeholder={undefined}
        style={style}
      />
    </b2x.FormGroup>
  );
};
